const TOOL_NAME_ALIASES = Object.freeze({
  desktop_apply_patch: 'patch_apply',
  desktop_write_file: 'files_write'
});
const WRITE_TOOL_NAMES = new Set(['files_write', 'patch_apply']);
const HIDDEN_TOOL_CALL_NAMES = new Set(['pieve_mcp_connections_setup', 'pieve_interactions_wait']);
const HIDDEN_ACTIVITY_FEED_TOOL_RESULT_NAMES = new Set(['pieve_mcp_action_schema', 'pieve_mcp_connections_setup', 'pieve_interactions_wait']);
const EMPTY_CONTENT_BLOCKED_ERROR_CODE = 'EMPTY_CONTENT_BLOCKED';

const toObjectOrNull = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};

const toCanonicalToolName = (toolName) => {
  const normalized = String(toolName || '')
    .trim()
    .toLowerCase();

  return TOOL_NAME_ALIASES[normalized] || normalized;
};

const getToolResultErrorCode = (output) => {
  const safeOutput = toObjectOrNull(output);

  if (!safeOutput) {
    return '';
  }

  const codeCandidates = [safeOutput?.code, safeOutput?.data?.code, safeOutput?.output?.code, safeOutput?.output?.data?.code];

  for (const candidate of codeCandidates) {
    const normalized = String(candidate || '').trim();

    if (normalized) {
      return normalized;
    }
  }

  return '';
};

const isEmptyContentBlockedOutput = (output) => {
  const normalizedCode = getToolResultErrorCode(output).toUpperCase();
  return normalizedCode === EMPTY_CONTENT_BLOCKED_ERROR_CODE;
};

const hasExplicitEmptyWriteContent = (args = {}) => {
  const safeArgs = toObjectOrNull(args) || {};
  const contentContainers = [safeArgs, safeArgs?.parsedArgs, safeArgs?.request, safeArgs?.request?.parsedArgs];

  for (const container of contentContainers) {
    const safeContainer = toObjectOrNull(container);

    if (!safeContainer || !Object.prototype.hasOwnProperty.call(safeContainer, 'content')) {
      continue;
    }

    if (safeContainer.content === null || safeContainer.content === undefined) {
      return true;
    }

    if (typeof safeContainer.content === 'string' && safeContainer.content.length < 1) {
      return true;
    }
  }

  return false;
};

const resolveToolResultOutputFromArgs = (args = {}) => {
  const safeArgs = toObjectOrNull(args) || {};
  const rootCandidates = [safeArgs?.response, safeArgs?.output, safeArgs?.result];

  for (const rootCandidate of rootCandidates) {
    const rootObject = toObjectOrNull(rootCandidate);

    if (!rootObject) {
      continue;
    }

    if (rootObject?.output && typeof rootObject.output === 'object' && !Array.isArray(rootObject.output)) {
      return rootObject.output;
    }

    return rootObject;
  }

  return null;
};

const shouldHideToolCallInActivityFeed = ({ toolName, args } = {}) => {
  const normalizedToolName = toCanonicalToolName(toolName);

  if (HIDDEN_TOOL_CALL_NAMES.has(normalizedToolName)) {
    return true;
  }

  if (!WRITE_TOOL_NAMES.has(normalizedToolName)) {
    return false;
  }

  if (hasExplicitEmptyWriteContent(args)) {
    return true;
  }

  const output = resolveToolResultOutputFromArgs(args);

  if (isEmptyContentBlockedOutput(output)) {
    return true;
  }

  return false;
};

const shouldHideToolResultInActivityFeed = ({ toolName, output } = {}) => {
  const normalizedToolName = toCanonicalToolName(toolName);

  if (HIDDEN_ACTIVITY_FEED_TOOL_RESULT_NAMES.has(normalizedToolName)) {
    return true;
  }

  if (!WRITE_TOOL_NAMES.has(normalizedToolName)) {
    return false;
  }

  return isEmptyContentBlockedOutput(output);
};

const shouldHideMessageInActivityFeed = ({ payload } = {}) => {
  return payload?.hidden === true;
};

export { shouldHideToolCallInActivityFeed, shouldHideToolResultInActivityFeed, shouldHideMessageInActivityFeed };
